import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import AnnounceBar from "./AnnounceBar";
import StickyPlayer from "../StickyPlayer";
import { useSiteContent } from "@/context/SiteContentContext";
import { pageSchemeFor } from "@/lib/pageScheme";
import { schemeHex } from "@/lib/schemes";

/**
 * Layout — the room. Each page chooses its own scheme from admin
 * (`page_scheme_home / _artists / _shop / _after-dark / _contact / …`),
 * making the site feel like distinct rooms inside one universe.
 *
 * The scheme exposes both:
 *  - Tailwind classes (bg / text) for the wrapper background + base text color
 *  - CSS variables (--sc-bg, --sc-text, --sc-subtext, --sc-accent, --sc-accent2,
 *    --sc-border, --sc-card) so every page/section can use `var(--sc-accent)`
 *    for the `.` dots, kickers, underlines, filter chips, media effects…
 */
export const Layout = () => {
    const { loaded, content } = useSiteContent();
    const location = useLocation();

    if (!loaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0E0E0E]" data-testid="site-splash">
                <div className="font-heading font-black text-3xl tracking-tighter text-[#F5F1E8]">
                    itzkatu
                </div>
            </div>
        );
    }

    const { scheme, bg, text } = pageSchemeFor(location.pathname, content);
    const hex = schemeHex(scheme);

    // Page width — one CSS var drives EVERY max-w-6xl container (header, footer,
    // announce and page sections) so navbar/footer/content margins always match.
    const PAGE_W = {
        xnarrow: "48rem",
        narrow:  "64rem",
        default: "72rem",
        wide:    "80rem",
        xwide:   "90rem",
        full:    "none",
    };
    const pageMaxW = PAGE_W[content.global_page_width] || PAGE_W.default;

    return (
        <div
            className={`grain min-h-screen flex flex-col ${bg} ${text}`}
            data-page-scheme={scheme}
            data-testid="site-layout"
            style={{
                "--page-max-w": pageMaxW,
                "--sc-bg": hex.bg,
                "--sc-text": hex.text,
                "--sc-subtext": hex.subtext,
                "--sc-accent": hex.accent,
                "--sc-accent2": hex.accent2,
                "--sc-border": hex.border,
                "--sc-card": hex.card,
            }}
        >
            <AnnounceBar />
            <Header />
            <main className="flex-1 w-full">
                <Outlet />
            </main>
            <Footer />
            <StickyPlayer />
        </div>
    );
};

export default Layout;
